<template>
  <VDialogV3
    id="manufacturer-card"
    position="right"
    width="600px"
    height="100vh"
    :entity="localManufacturer"
    :file-entities="fileEntities"
    :context-menu="ManufacturersService.contextMenu"
    :submit-disabled="saveDisabled"
    :submit-method="submit"
  >
    <section>
      <VInput
        ref="nameInput"
        v-model="localManufacturer.name"
        label="Название производителя"
        layout="vertical"
        :validate-method="validateName"
      />
    </section>

    <br>

    <section class="trade-marks">
      <h4>Торговые марки</h4>

      <!-- Строка — выбор существующей марки или ввод новой: новая уходит на бэк
           только с name (:type → TradeMark.fromSuggestion), бэк создаёт её сам.
           Дубли не проверяем: уникальность держит бэк и вернёт ошибку -->
      <div
        v-for="(tradeMark, index) in localManufacturer.tradeMarks"
        :key="index"
        class="trade-mark-row"
      >
        <VSuggestionSelectV2
          v-model="localManufacturer.tradeMarks[index]"
          :type="TradeMark"
          :data="({ pattern }: { pattern: string }) => TradeMarksService.getList({ pattern })"
          placeholder="Название торговой марки"
          required
        />
        <VButton
          material="close"
          title="Удалить"
          @click="localManufacturer.tradeMarks.splice(index, 1)"
        />
      </div>

      <VButton
        theme="link"
        material="add"
        @click="addTradeMarkRow"
      >
        Добавить торговую марку
      </VButton>
    </section>
  </VDialogV3>
</template>

<style lang="scss">
#manufacturer-card {
  .trade-marks {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .trade-mark-row {
    display: flex;
    align-items: center;
    gap: 8px;

    > .mds-select {
      flex: 1;
    }
  }
}
</style>

<script lang="ts">

import Manufacturer            from '@/domains/Manufacturers/Manufacturer';
import ManufacturersService    from '@/domains/Manufacturers/ManufacturersService';
import TradeMark               from '@/domains/TradeMarks/TradeMark';
import TradeMarksService       from '@/domains/TradeMarks/TradeMarksService';
import EntityPointer           from '@/domains/EntityPointer/EntityPointer';

export default defineComponent({
  name: 'ManufacturerCard',

  props: {
    manufacturer: {
      type:    Manufacturer as PropType<Manufacturer | null>,
      default: null,
    },
    id: {
      type:    Number,
      default: null,
    },
  },

  setup() {
    return {
      ManufacturersService,
      TradeMark,
      TradeMarksService,
    };
  },

  data() {
    return {
      localManufacturer: null as Manufacturer | null,

      /** Сохранённое название — своё же название дублем не считается */
      initialName: null as string | null,

      /** Производитель как открыли — с ним сравниваем, были ли изменения (isManufacturerChanged) */
      initialManufacturer: null as Manufacturer | null,
    };
  },

  computed: {
    /**
     * Причина запрета сохранения (строка → :submit-disabled показывает тултип).
     * Пустую строку марки не сохраняем молча: пользователь должен её заполнить или удалить,
     * иначе он увидит «обновлено», а марка не добавится.
     */
    saveDisabled(): string | null {
      const hasEmptyTradeMark = this.localManufacturer?.tradeMarks.some(
        tradeMark => tradeMark === null || (tradeMark.name ?? '').trim() === ''
      );

      if (hasEmptyTradeMark === true) {
        return 'Заполните или удалите пустую торговую марку';
      }
      else {
        return null;
      }
    },

    /**
     * Есть ли что сохранять — глубокое сравнение с производителем как открыли (как в StoreCard).
     * Перестановку марок или повторный выбор той же марки сочтёт изменением —
     * тогда уйдёт лишний update с теми же данными, это безвредно.
     */
    isManufacturerChanged(): boolean {
      return this.$core.utils.isEqual(this.localManufacturer, this.initialManufacturer) === false;
    },

    /** Указатели на сущности, к которым цепляются файлы: только сам производитель. Пустые отсеиваются. */
    fileEntities(): EntityPointer[] {
      return EntityPointer.collect([
        EntityPointer.make('manufacturer', this.localManufacturer?.id, this.localManufacturer?.toString()),
      ]);
    },
  },

  async mounted() {
    await this.init();
  },

  methods: {
    async init(): Promise<void> {
      if (this.id) {
        this.localManufacturer = await ManufacturersService.get(this.id);
      }
      else if (this.manufacturer instanceof Manufacturer) {
        this.localManufacturer = this.$core.utils.cloneDeep(this.manufacturer);
      }
      else {
        this.localManufacturer = new Manufacturer();
      }

      this.initialName         = this.localManufacturer.name;
      this.initialManufacturer = this.$core.utils.cloneDeep(this.localManufacturer);
    },

    /**
     * Сохранение. Ничего не меняли — запрос не шлём и возвращаем true:
     * VDialogV3 закроет карточку как после сохранения, без «Производитель обновлён».
     */
    async submit(): Promise<boolean> {
      if (this.isManufacturerChanged === false) {
        return true;
      }
      else if (this.localManufacturer!.isExisting === true) {
        return await ManufacturersService.update(this.localManufacturer!);
      }
      else {
        return await ManufacturersService.add(this.localManufacturer!);
      }
    },

    /** Пустая строка под новую марку: пока её не заполнят или не удалят, сохранение заблокировано (saveDisabled) */
    addTradeMarkRow(): void {
      (this.localManufacturer!.tradeMarks as (TradeMark | null)[]).push(null);
    },

    validateName(value: string): string | null | Promise<string | null> {
      if (typeof value !== 'string' || value.length === 0) { return 'Поле обязательно'; }
      if (value.length > 200) { return 'Максимальная длина поля 200 символов'; }
      if (value === this.initialName) { return null; }

      return ManufacturersService.getList({ name: value }).then(list => {
        return list.length > 0 ? 'Ошибка уникальности. Данное название уже существует' : null;
      });
    },
  },
});
</script>
